import type { Parchment } from 'quill/core';
import type Quill from 'quill/core';
import React, { createContext, useContext, useMemo } from 'react';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import Logger from '../utils/Logger';
import safeParseUrl from '../utils/url/safeParseUrl';

const ASSET_TOKEN_PROVIDER_ENTITY_ASSET_TOKEN_FRAGMENT = gql(`
  fragment AssetTokenProvider_entityAssetToken on EntityAssetToken {
    token
  }
`);

type AssetTokenFragment = DocumentType<
  typeof ASSET_TOKEN_PROVIDER_ENTITY_ASSET_TOKEN_FRAGMENT
>;

const AssetTokenContext = createContext<{
  entityAssetToken?: AssetTokenFragment;
} | null>(null);

interface AssetTokenProviderProps {
  children: React.ReactNode;
  entityAssetToken: AssetTokenFragment;
}
export const AssetTokenProvider = ({
  children,
  entityAssetToken,
}: AssetTokenProviderProps) => {
  const value = useMemo(() => ({ entityAssetToken }), [entityAssetToken]);
  return (
    <AssetTokenContext.Provider value={value}>
      {children}
    </AssetTokenContext.Provider>
  );
};

const reportNoAssetTokenError = () => {
  const error =
    'Asset token is not available. Make sure AssetTokenProvider is in the component tree';
  if (process.env.NODE_ENV === 'production') {
    Logger.error(error);
  } else {
    throw new Error(error);
  }
};

export const useAssetTokenContext = () => {
  const context = useContext(AssetTokenContext);

  if (!context) {
    reportNoAssetTokenError();
    return { entityAssetToken: null };
  }
  return context;
};

const SCROLL_TOKEN_MAP = new WeakMap<Parchment.Root, string>();
export const useLinkAssetTokenToQuill = () => {
  const context = useContext(AssetTokenContext);
  const assetToken = context?.entityAssetToken?.token;
  return (quill: Quill) => {
    if (assetToken) {
      SCROLL_TOKEN_MAP.set(quill.scroll, assetToken);
    }
  };
};

export const getAssetTokenFromBlot = (blot: Parchment.Blot) => {
  const assetToken = SCROLL_TOKEN_MAP.get(blot.scroll);
  if (!assetToken) {
    reportNoAssetTokenError();
  }

  return assetToken ?? null;
};

export const removeAssetTokenFromSourceUrl = (sourceUrl: string) => {
  const url = safeParseUrl(sourceUrl);
  if (!url) return sourceUrl;
  url.searchParams.delete('jwt');
  return url.toString();
};

export const getSourceUrlWithAssetToken = (
  source: string,
  token: string | null = null,
) => {
  if (!token) return source;

  const url = safeParseUrl(source);
  if (!url) return source;

  if (url.protocol !== 'data:') {
    url.searchParams.append('jwt', token);
  }
  return url.toString();
};

const useSourceWithAssetToken = <S extends string | null>(source: S) => {
  const { entityAssetToken } = useAssetTokenContext();

  return source
    ? getSourceUrlWithAssetToken(source, entityAssetToken?.token)
    : source;
};

export default useSourceWithAssetToken;
